/**
 * Lazy workspace-tab seam.
 *
 * Heavy tab surfaces (Schema Compare, Data Import, Job Scheduler, Blueprint, …)
 * are NOT part of the first-paint path — a boot only ever renders Query Editor,
 * Table Explorer or the Start Page. Registering them through this factory keeps
 * each surface (and the npm libs only it pulls: recharts, @xyflow/react,
 * sql-formatter, …) in its own Vite chunk that is fetched on the tab's first
 * mount instead of being bundled into the eager renderer entry.
 *
 * Same shape as `spatial/map/SpatialMapLazy.tsx` (the repo's first `React.lazy`):
 * a `lazy()` component behind a `Suspense` whose fallback is the repo-standard
 * `RandomDotmSpinner` (never `Loader2`).
 *
 * Tab STATE is untouched by this: the workspace store owns tab identity and
 * content, so a lazily-mounted surface has exactly the same semantics as an
 * eager one — the fallback is visible only while the chunk is in flight on the
 * first open of that surface type.
 */

import { lazy, Suspense, type ComponentType } from 'react';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import type { WorkspaceTabComponentProps } from '../types/workspaceTypes';
import type { WorkspaceTabComponent } from './workspaceRegistry';

type TabModuleLoader = () => Promise<{
  default: ComponentType<WorkspaceTabComponentProps>;
}>;

/**
 * Wrap a dynamic `import()` of a workspace tab component into a registry-ready
 * component. Named exports need the usual `.then(m => ({ default: m.X }))`
 * adapter at the call site.
 */
export function lazyWorkspaceTab(loader: TabModuleLoader): WorkspaceTabComponent {
  const LazyTab = lazy(loader);

  return function LazyWorkspaceTab(props: WorkspaceTabComponentProps) {
    return (
      <Suspense
        fallback={
          <div
            className="flex h-full w-full items-center justify-center bg-background"
            data-testid="workspace-tab-loading"
          >
            <RandomDotmSpinner size={24} />
          </div>
        }
      >
        <LazyTab {...props} />
      </Suspense>
    );
  };
}
